import { SharedRehydrationProps } from 'components/_shared/widgets/rehydration/types';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PersistState } from 'redux-persist';
import { RootState } from 'typesafe-actions';
import { HydrationStateEnum } from 'types/redux';
import { isPersistedState } from 'types/guards/redux';

const REHYDRATION_TIMEOUT = 1500;

interface ReduxPersist {
  _persist?: PersistState | unknown;
}

type RootStateWithPersist = {
  [K in keyof RootState]: RootState[K] & ReduxPersist;
};

/**
 * NOTE: keeping this hook isolated to this component for performance purposes.
 * The hook needs to listen to the entire root state, but once it's "completed" the first time it's no longer needed.
 * The only way to remove the extra renders is to dismount the component that's using this hook as soon as it's done.
 * So we're tying the hook directly to this component for managing that dismount.
 */
const useAreReduxStatesRehydrated = (
  statesToCheck: HydrationStateEnum[]
): {
  rehydrated: boolean;
  timedOut: boolean;
} => {
  const rootState: RootStateWithPersist = useSelector(
    (state: RootState) => state
  );

  const rehydrated = useMemo(
    () =>
      statesToCheck
        .map(s => rootState[s])
        .every(s => isPersistedState(s._persist) && s._persist.rehydrated),
    [rootState, statesToCheck]
  );

  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (rehydrated) return;
    const timeoutId = setTimeout(() => setTimedOut(true), REHYDRATION_TIMEOUT);
    return () => clearTimeout(timeoutId);
  }, [rehydrated]);

  // we dismount the parent component once "done" so we don't need to memoize this object
  return { rehydrated, timedOut };
};

type RehydrationWatcherProps = SharedRehydrationProps & {
  onRehydrate: () => void;
  onTimeOut?: () => void;
};

const RehydrationWatcher = ({
  onRehydrate,
  onTimeOut,
  dismount,
  watchedStates,
}: RehydrationWatcherProps & { dismount: () => void }) => {
  const statesRehydrated = useAreReduxStatesRehydrated(
    Array.isArray(watchedStates) ? watchedStates : [watchedStates]
  );

  useEffect(() => {
    const { rehydrated, timedOut } = statesRehydrated;
    if (rehydrated) {
      onRehydrate();
      dismount();
    } else if (timedOut) {
      onTimeOut && onTimeOut();
      dismount();
    }
  }, [onRehydrate, onTimeOut, dismount, statesRehydrated]);

  return null;
};

const Wrapper = (props: RehydrationWatcherProps) => {
  const [isComplete, setIsComplete] = useState(false);
  const dismount = useCallback(() => setIsComplete(true), []);
  if (isComplete) return null;
  return <RehydrationWatcher {...props} dismount={dismount} />;
};

export default Wrapper;
